import React, { useEffect, useMemo, useRef, useState } from "react";
import { Add, Delete, Star } from '@mui/icons-material';

import COLOR from "utils/constants/color";

import {Cancel} from '@mui/icons-material';
import Loading from "components/Loading";
import Tag from "components/Tag";
import styled from "styled-components";
import { Row } from "antd";
import { map } from "lodash";
import { v4 as uuid } from "uuid";
import { styled as muiStyled } from '@mui/material/styles';
import SentryWrap from 'components/Sentry';

import {
  Button,
  Checkbox,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Modal,
  Select,
  TextField,
} from '@mui/material';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

const StyledFormControl = muiStyled(FormControl)(({ theme }) => ({
  marginBottom: "25px",
  width: "300px",
}));

const StyledFormControlDif = muiStyled(FormControl)({
  width: "200px",
});

const StyledFormControlDifFull = muiStyled(FormControl)({
  width: "100%",
});

const StyledFormControlStudent = muiStyled(FormControl)(({ theme }) => ({
  backgroundColor: "#fff",
  width: "400px",
  margin: '2px',
  minWidth: 120,
}));

const StyledCheckbox = muiStyled(Checkbox)({
  '&.Mui-indeterminate': {
    color: "#f50057",
  },
});

const StyledMenuItem = muiStyled(MenuItem)({
  '&.selected-all': {
    backgroundColor: "rgba(0, 0, 0, 0.08)",
    '&:hover': {
      backgroundColor: "rgba(0, 0, 0, 0.08)",
    },
  },
});

const StyledButton = muiStyled(Button)({
  '&.light-grey': {
    backgroundColor: "#d3d3d3",
    marginRight: "4px",
    '&:hover': {
      backgroundColor: "#bfbfbf",
    },
  },
});

const StyledListItemText = muiStyled(ListItemText)({
  '& .MuiTypography-root': {
    fontWeight: 500,
  },
});

const StyledChip = muiStyled(Chip)({
  color: `${COLOR.green}!important`,
});

const MultiMenu = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
  variant: "menu",
  getContentAnchorEl: () => null,
};

const WidgetEditModal = ({
  open,
  users,
  selectedUser,
  handlerDone,
  close,
  loader,
  resetAllList,
}) => {
  const lastItemRef = useRef(null);

  // State for managing users in the modal or State for managing selected usernames
  const [modalUsers, setModalUsers] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);

  // new user entries
  const [newUserList, setNewUserList] = useState([]);

  useEffect(() => {
    if(lastItemRef?.current){
      lastItemRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [newUserList]);

  useEffect(() => {
    const initialUsers = users || [];
    setModalUsers(initialUsers);

    const initialSelectedIds = selectedUser?.map((user) => user.id) || [];
    setSelectedIds(initialSelectedIds);
  }, [users, selectedUser, open]);

  useEffect(()=> {
    setNewUserList([]);
  },[open])

  const isAllSelected = useMemo(() => {
    return modalUsers.length > 0 && selectedIds.length === modalUsers.length;
  }, [modalUsers.length, selectedIds.length]);

  const handleSelectionChange = (event) => {
    const value = event.target.value;

    if (value.includes("all")) {
      if (isAllSelected) {
        setSelectedIds([]);
      } else {
        const allIds = modalUsers.map((user) => user.id);
        setSelectedIds(allIds);
      }
      return;
    }
    setSelectedIds(value);
  };

  const isNewUserFormValid = useMemo(() => {
    return newUserList.every(
      (user) =>
        user.firstName?.trim().length > 0 || user.lastName?.trim().length > 0
    );
  }, [newUserList]);

  const addNewUserForm = () => {
    setNewUserList((prev) => [
      ...prev,
      { firstName: "", lastName: "", id: uuid() },
    ]);
  };

  const removeUserForm = (id) => {
    setNewUserList((prev) => prev.filter((user) => user.id !== id));
  };

  const handleInputChange = (event, id) => {
    const { name, value } = event.target;
    setNewUserList((prev) =>
      prev.map((user) => (user.id === id ? { ...user, [name]: value } : user))
    );
  };

  const handleAddNewUsers = async () => {
    const newUsers = newUserList.map((user) => ({
      id: user.id,
      role: "student",
      userName: `${user.lastName} ${user.firstName}`.trim(),
    }));

    const modalUser = [...modalUsers, ...newUsers]; 
    const selectedUser =  [...selectedIds, ...newUsers.map((user) => user.id)];
    setModalUsers((prev) => [...prev, ...newUsers]);
    setSelectedIds((prev) => [...prev, ...newUsers.map((user) => user.id)]);
    setNewUserList([]);

    const selectedUsers = modalUser.filter((user) =>
      selectedUser.includes(user.id)
    );
    await handlerDone(modalUser, selectedUsers, "custom-done");
  };

  const handleDone = async () => {
    if(newUserList?.length > 0){
       await handleAddNewUsers();
       return;
    }
    setNewUserList([]);
    const selectedUsers = modalUsers.filter((user) =>
      selectedIds.includes(user.id)
    );
    handlerDone(modalUsers, selectedUsers);
  };

  const checkDisableCheck = useMemo(()=> {
    if(newUserList?.length > 0){
     return newUserList.some((element) => {
        const trimmedFirstName = element?.firstName?.trim() || '';
        const trimmedLastName = element?.lastName?.trim() || '';
        // const hasBothEmpty = trimmedFirstName.length === 0 && trimmedLastName.length === 0;
        const hasBothEmpty = trimmedFirstName.length === 0
        return hasBothEmpty;
      });   
    }
    else if(selectedIds?.length === 0) {
      return selectedIds?.length === 0
    }
  },[newUserList, selectedIds])

  return (
  <SentryWrap>
    <Modal
      open={open}
      onClose={close}
      disableEscapeKeyDown
      BackdropProps={{
        onClick: (e) => {
          e.stopPropagation();
        },
      }}
      className={`addStudentModel ${open ? "disable-key-navigation" : ""}`}
    >
      <ModalContent>
        <Cancel
          className="cancel-icon-image-for-edit-modal"
          onClick={() => close()}
        />
        <StyledFormControlStudent
          variant="outlined"
          style={{ width: "400px", backgroundColor: "#fff" }}
        >
          {/* New User Forms */}
          <div
            style={{
              border: newUserList?.length === 0 && "none",
              margin: newUserList?.length === 0 && "0px",
              padding: newUserList?.length === 0 && "0px",
            }}
            className="w-full form-field-container"
          >
            {map(newUserList, (user, index) => (
              <ModalRow
                key={user.id}
                ref={index === newUserList.length - 1 ? lastItemRef : null}
              >
                <TextField
                  name="firstName"
                  label="First Name"
                  style={{ width: 150 }}
                  variant="outlined"
                  value={user.firstName}
                  onChange={(e) => handleInputChange(e, user.id)}
                  InputLabelProps={{ shrink: true, required: true }}
                />
                <TextField
                  name="lastName"
                  label="Last Name"
                  style={{ width: 150 }}
                  variant="outlined"
                  value={user.lastName}
                  onChange={(e) => handleInputChange(e, user.id)}
                  InputLabelProps={{ shrink: true }}
                />
                <IconButton
                  onClick={() => removeUserForm(user.id)}
                  color="secondary"
                  className="newStationDelete"
                >
                  <Delete />
                </IconButton>
              </ModalRow>
            ))}
          </div>

          {/* Add Student Button */}
          <ModalRow>
            <Button
              className="widgetAddStudent" 
              color="primary"
              variant="contained"
              onClick={addNewUserForm}
            >
              <Add /> Add student
            </Button>

            {/* Action Buttons */}
            <Row align={"end"}>
              {(newUserList?.length > 0 || selectedIds?.length === 0) && (
                <Button
                  className="light-grey wheelClose"
                  component={StyledButton}
                  onClick={() => {
                    if (newUserList?.length > 0) {
                      setNewUserList([]);
                      return;
                    } else if (selectedIds?.length === 0) {
                      close();
                      return;
                    }
                  }}
                >
                  {newUserList?.length > 0 ? "Cancel" : "Close"}
                </Button>
              )}

              <Button
                color="primary"
                variant="contained"
                style={{ marginRight: "4px" }}
                onClick={resetAllList}
                disabled={loader?.type === "reset-list" && loader?.value}
              >
                {loader?.type === "reset-list" && loader?.value && (
                  <Loading white={true} size={15} />
                )}
                Reset List
              </Button>

              <Button
                color="primary"
                variant="contained"
                onClick={handleDone}
                disabled={
                  checkDisableCheck ||
                  (loader?.type === "save-loader" && loader?.value)
                }
              >
                {loader?.type === "save-loader" && loader?.value && (
                  <Loading white={true} size={15} />
                )}

                {newUserList?.length > 0 ? "Done" : "Save"}
              </Button>
            </Row>
          </ModalRow>

          {/* Student Selection */}
          <FormControl
            variant="outlined"
            style={{ width: "100%" }}
            className={StyledFormControlStudent}
          >
            <InputLabel style={{ backgroundColor: "#fff" }}>
              Select students
            </InputLabel>
            <Select
              multiple
              value={selectedIds}
              onChange={handleSelectionChange}
              renderValue={(selected) => (
                <div className={StyledChip}>
                  <Chip
                    label={
                      isAllSelected || selected.length === 0
                        ? "All students"
                        : `Students(${selected.length})`
                    }
                    className={StyledChip}
                  />
                </div>
              )}
              MenuProps={MultiMenu}
            >
              <StyledMenuItem
                value="all"
                className={`widgetsSelectAll ${isAllSelected ? "selected-all widgetMenuAll" : ""}`}
              >
                <ListItemIcon>
                  <StyledCheckbox
                    checked={isAllSelected}
                    indeterminate={
                      selectedIds.length > 0 &&
                      selectedIds.length < modalUsers.length
                    }
                  />
                </ListItemIcon>
                <StyledListItemText primary="Select All" />
              </StyledMenuItem>
              {modalUsers.map((user) => (
                <MenuItem className="widgetMenu" key={user.id} value={user.id}>
                  <ListItemIcon> 
                    <Checkbox checked={selectedIds.includes(user.id)} />
                  </ListItemIcon>
                  <ListItemText primary={user.userName} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Note */}
          <div className="flex items-center mt-10px widgetNote">
            <Tag height={20}>
              <Star
                style={{
                  height: "12px",
                  margin: "-1px 4px 0px 0px",
                  width: "12px",
                }}
              />
              NOTE
            </Tag>

            <Note>
              The list saves for 12 hours after clicking the save button and
              List will be reset after 12 hour.
            </Note>
          </div>
        </StyledFormControlStudent>
      </ModalContent>
    </Modal>
  </SentryWrap>
  );
};

export default WidgetEditModal;

const CancelImage = styled(Cancel)`
    z-Index: 1,
    margin-left: 5px;
    position: absolute;
    right: 11;
    top: 14;
    fontSize: 20;
    color: red;
    background: white;
    border-radius: 20;
    cursor: pointer;
`;

const ModalRow = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 16px;
`;

const ModalContent = styled.div`
  align-items: center;
  background-color: ${COLOR.white};
  border-radius: 3px;
  display: inline-flex;
  flex-direction: column;
  left: 50%;
  padding: 16px;
  position: absolute;
  text-align: center;
  top: 50%;
  transform: translate(-50%, -50%);
`;

const Note = styled.span`
  color: #000;
  display: block;
  font-size: 13px;
  font-weight: 400;
  line-height: 20px;
`;